import { CardId } from './types';

export interface CardConfig {
  id: CardId;
  defaultWidth: number;
  defaultHeight: number;
  minWidth?: number;
  minHeight?: number;
  availableInViews: ('manager' | 'my')[];
  category?: string;
  removable?: boolean; // Whether user can remove this card
}

export const CARD_REGISTRY: Record<CardId, CardConfig> = {
  totalEntities: {
    id: 'totalEntities',
    defaultWidth: 2,
    defaultHeight: 2,
    minWidth: 2,
    minHeight: 2,
    availableInViews: ['manager', 'my'],
    category: 'summary',
    removable: true,
  },
  totalReturns: {
    id: 'totalReturns',
    defaultWidth: 2,
    defaultHeight: 2,
    minWidth: 2,
    minHeight: 2,
    availableInViews: ['my'],
    category: 'summary',
    removable: true,
  },
  notStarted: {
    id: 'notStarted',
    defaultWidth: 2,
    defaultHeight: 2,
    minWidth: 2,
    minHeight: 2,
    availableInViews: ['manager', 'my'],
    category: 'summary',
    removable: true,
  },
  pendingTasks: {
    id: 'pendingTasks',
    defaultWidth: 2,
    defaultHeight: 2,
    minWidth: 2,
    minHeight: 2,
    availableInViews: ['manager'],
    category: 'summary',
    removable: true,
  },
  awaitingReview: {
    id: 'awaitingReview',
    defaultWidth: 2,
    defaultHeight: 2,
    minWidth: 2,
    minHeight: 2,
    availableInViews: ['manager'],
    category: 'summary',
    removable: true,
  },
  submittedThisMonth: {
    id: 'submittedThisMonth',
    defaultWidth: 2,
    defaultHeight: 2,
    minWidth: 2,
    minHeight: 2,
    availableInViews: ['manager', 'my'],
    category: 'summary',
    removable: true,
  },
  tasksByStatus: {
    id: 'tasksByStatus',
    defaultWidth: 4,
    defaultHeight: 6,
    minWidth: 3,
    minHeight: 4,
    availableInViews: ['manager', 'my'],
    category: 'informationCards',
    removable: true,
  },
  returnsByDueDate: {
    id: 'returnsByDueDate',
    defaultWidth: 4,
    defaultHeight: 6,
    minWidth: 3,
    minHeight: 4,
    availableInViews: ['manager', 'my'],
    category: 'informationCards',
    removable: true,
  },
  calendar: {
    id: 'calendar',
    defaultWidth: 4,
    defaultHeight: 6,
    minWidth: 3,
    minHeight: 4,
    availableInViews: ['manager', 'my'],
    category: 'informationCards',
    removable: true,
  },
  typesOfReturns: {
    id: 'typesOfReturns',
    defaultWidth: 4,
    defaultHeight: 4,
    minWidth: 3,
    minHeight: 4,
    availableInViews: ['manager', 'my'],
    category: 'informationCards',
    removable: true,
  },
  yourRoleInReturns: {
    id: 'yourRoleInReturns',
    defaultWidth: 4,
    defaultHeight: 4,
    minWidth: 3,
    minHeight: 4,
    availableInViews: ['my'],
    category: 'informationCards',
    removable: true,
  },
  vatNumberChecker: {
    id: 'vatNumberChecker',
    defaultWidth: 4,
    defaultHeight: 4,
    minWidth: 3,
    minHeight: 4,
    availableInViews: ['my'],
    category: 'informationCards',
    removable: true,
  },
  teamAllocation: {
    id: 'teamAllocation',
    defaultWidth: 8,
    defaultHeight: 6,
    minWidth: 3,
    minHeight: 4,
    availableInViews: ['manager'],
    category: 'team',
    removable: true,
  },
  submittedReturnsChart: {
    id: 'submittedReturnsChart',
    defaultWidth: 12,
    defaultHeight: 11,
    minWidth: 6,
    minHeight: 6,
    availableInViews: ['manager', 'my'],
    category: 'informationCards',
    removable: true,
  },
  timeToSubmit: {
    id: 'timeToSubmit',
    defaultWidth: 12,
    defaultHeight: 6,
    minWidth: 6,
    minHeight: 6,
    availableInViews: ['manager'],
    category: 'team',
    removable: true,
  },
  capturePendingReview: {
    id: 'capturePendingReview',
    defaultWidth: 2,
    defaultHeight: 2,
    minWidth: 2,
    minHeight: 2,
    availableInViews: ['manager', 'my'],
    category: 'capture',
    removable: true,
  },
  captureReadyToPost: {
    id: 'captureReadyToPost',
    defaultWidth: 2,
    defaultHeight: 2,
    minWidth: 2,
    minHeight: 2,
    availableInViews: ['manager', 'my'],
    category: 'capture',
    removable: true,
  },
  capturePostedFiles: {
    id: 'capturePostedFiles',
    defaultWidth: 2,
    defaultHeight: 2,
    minWidth: 2,
    minHeight: 2,
    availableInViews: ['manager', 'my'],
    category: 'capture',
    removable: true,
  },
  captureFilesUploaded: {
    id: 'captureFilesUploaded',
    defaultWidth: 2,
    defaultHeight: 2,
    minWidth: 2,
    minHeight: 2,
    availableInViews: ['manager', 'my'],
    category: 'capture',
    removable: true,
  },
};

// Get all available cards for a view type
export const getAvailableCardsForView = (
  viewType: 'manager' | 'my'
): CardConfig[] => {
  return Object.values(CARD_REGISTRY).filter((card) =>
    card.availableInViews.includes(viewType)
  );
};

// Get default card order for a view type
export const getDefaultCardOrderForView = (
  viewType: 'manager' | 'my'
): CardId[] => {
  if (viewType === 'my') {
    return [
      'totalEntities',
      'totalReturns',
      'notStarted',
      'submittedThisMonth',
      'captureFilesUploaded',
      'capturePendingReview',
      'captureReadyToPost',
      'capturePostedFiles',
      'tasksByStatus',
      'returnsByDueDate',
      'calendar',
      'typesOfReturns',
      'yourRoleInReturns',
      'vatNumberChecker',
      'submittedReturnsChart',
    ];
  }

  return [
    'totalEntities',
    'notStarted',
    'pendingTasks',
    'awaitingReview',
    'captureFilesUploaded',
    'capturePendingReview',
    'captureReadyToPost',
    'capturePostedFiles',
    'submittedThisMonth',
    'tasksByStatus',
    'returnsByDueDate',
    'calendar',
    'teamAllocation',
    'typesOfReturns',
    'timeToSubmit',
    'submittedReturnsChart',
  ];
};

/** Default visible card order for a view (registry is source of truth). */
export const getDefaultDashboardCardOrderForView = (
  viewType: 'manager' | 'my'
): CardId[] =>
  getDefaultCardOrderForView(viewType).filter((cardId) =>
    CARD_REGISTRY[cardId]?.availableInViews.includes(viewType)
  );

